import React, { useState } from 'react';
import { Modal, Button } from '../ui';
import { X, ArrowRightLeft } from 'lucide-react';
import type { Table } from '../../types/@types/table';

interface TransferTableModalProps {
  open: boolean;
  tables: Table[];
  currentTable: Table | null;
  onClose: () => void;
  onConfirm: (targetTableId: string) => void;
  loading?: boolean;
}

export const TransferTableModal: React.FC<TransferTableModalProps> = ({
  open,
  tables,
  currentTable,
  onClose,
  onConfirm,
  loading,
}) => {
  const [targetTableId, setTargetTableId] = useState<string>('');

  const handleConfirm = () => {
    if (!targetTableId) return;
    onConfirm(targetTableId);
  };

  // Only allow transferring to a table that is "Disponible" (Available) and not the current table
  const availableTables = tables.filter(
    (t) => t.status === 'Disponible' && String(t.id) !== String(currentTable?.id)
  );

  return (
    <Modal open={open} onClose={onClose} titleId="transfer-modal-title" className="max-w-md">
      <div className="flex items-start justify-between border-b border-border px-6 py-4">
        <div>
          <h2 id="transfer-modal-title" className="text-lg font-semibold text-text">
            Transferir Pedido de Mesa
          </h2>
          <p className="text-sm text-text-muted">
            Mueve el pedido actual de la **Mesa #{currentTable?.number}** a una mesa libre.
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose} className="h-9 w-9 rounded-full">
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="p-6 space-y-4">
        <label className="block text-sm font-medium text-text">
          Selecciona la Mesa de Destino (Solo Disponibles)
        </label>
        {availableTables.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-text-muted">
            No hay otras mesas libres en este momento.
          </div>
        ) : (
          <div className="grid grid-cols-4 gap-2 max-h-[240px] overflow-y-auto pr-1">
            {availableTables.map((t) => (
              <button
                key={`target-${t.id}`}
                onClick={() => setTargetTableId(t.id)}
                className={`p-3 rounded-xl border text-sm font-bold transition-all ${
                  targetTableId === t.id
                    ? 'bg-primary border-primary text-primary-fg shadow-md scale-95'
                    : 'bg-surface border-border text-text hover:bg-muted/40'
                }`}
              >
                #{t.number}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="border-t border-border px-6 py-4 flex gap-3 justify-end">
        <Button variant="outline" onClick={onClose}>
          Cancelar
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={!targetTableId || loading}
          isLoading={loading}
          className="bg-primary text-primary-fg"
        >
          <ArrowRightLeft className="mr-2 h-4 w-4" />
          Transferir Ahora
        </Button>
      </div>
    </Modal>
  );
};

export default TransferTableModal;
